if (!customElements.get("m-collection-list")) {
  class MCollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
        slideInner: ".m-mixed-layout__inner",
      };

      this.classes = {
        grid: "m:grid",
        hidden: "m:hidden",
        swiper: "swiper-container",
        swiperWrapper: "swiper-wrapper",
      };

      this.domNodes = queryDomNodes(this.selectors, this);

      this.slider = false;

      this.enableSlider = this.dataset.enableSlider === "true";
      this.items = parseInt(this.dataset.items) || 2;
      this.total = parseInt(this.dataset.total) || 0;

      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5;

      this.paginationType = this.dataset.paginationType;
      this.expanded = this.dataset.expanded === "true";
      this.mobileDisableSlider =
        this.dataset.mobileDisableSlider === "true";

      // Smooth continuous mode
      this.smoothScroll = this.dataset.smoothScroll === "true";

      this.smoothScrollSpeed =
        parseInt(this.dataset.smoothScrollSpeed) || 6000;

      // Safety limits
      this.smoothScrollSpeed = Math.max(
        2500,
        Math.min(this.smoothScrollSpeed, 15000)
      );

      // Progress line duration
      this.style.setProperty(
        "--collection-flow-duration",
        `${this.smoothScrollSpeed}ms`
      );

      this.init();

      document.addEventListener("matchMobile", () => {
        this.init();
      });

      document.addEventListener("unmatchMobile", () => {
        this.init();
      });
    }

    init() {
      if (!this.enableSlider) return;

      if (
        MinimogTheme.config.mqlMobile &&
        this.mobileDisableSlider
      ) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const {
        slideContainer,
        slideControls,
        slideInner,
      } = queryDomNodes(this.selectors, this);

      if (!slideContainer || !slideInner) return;

      const prevButton =
        slideControls &&
        slideControls.querySelector(
          ".m-slider-controls__button-prev"
        );

      const nextButton =
        slideControls &&
        slideControls.querySelector(
          ".m-slider-controls__button-next"
        );

      const paginationIcon = `
        <svg
          width="65px"
          height="65px"
          viewBox="0 0 72 72"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            class="time"
            stroke-width="5"
            fill="none"
            stroke-linecap="round"
            cx="33"
            cy="33"
            r="28"
          ></circle>
        </svg>
      `;

      const smoothMode = this.smoothScroll;

      this.options = {
        slidesPerView: 2,

        loop: true,

        slidesPerGroup: 1,

        // Normal = standard transition
        // Smooth = long linear movement
        speed: smoothMode
          ? this.smoothScrollSpeed
          : 300,

        // Keep touch/swipe support
        allowTouchMove: true,

        // Extra cloned slides make looping smoother
        loopAdditionalSlides: smoothMode
          ? Math.max(this.items, 4)
          : 0,

        parallax: smoothMode ? false : true,

        /*
         * SMOOTH MODE
         * delay 0 = no pause between slides
         *
         * NORMAL MODE
         * existing autoplay behaviour remains
         */
        autoplay: smoothMode
          ? {
              delay: 0,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }
          : this.autoplay
          ? {
              delay: this.autoplaySpeed * 1000,
              disableOnInteraction: false,
            }
          : false,

        /*
         * Normal pagination remains available.
         * Smooth mode uses our lightweight progress line.
         */
        pagination: smoothMode
          ? false
          : this.paginationType === "fraction"
          ? {
              el: this.querySelector(
                ".swiper-pagination"
              ),
              clickable: true,
              type: "fraction",
            }
          : {
              el: this.querySelector(
                ".swiper-pagination"
              ),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",

              renderBullet: function (
                index,
                className
              ) {
                return (
                  '<span class="' +
                  className +
                  '">' +
                  paginationIcon +
                  "</span>"
                );
              },
            },

        navigation: smoothMode
          ? false
          : nextButton || prevButton
          ? {
              nextEl: nextButton || undefined,
              prevEl: prevButton || undefined,
            }
          : false,

        // Avoid repeated height calculations in continuous mode
        autoHeight: smoothMode ? false : true,

        breakpoints: {
          480: {
            slidesPerView: 3,
          },

          768: {
            slidesPerView:
              this.items >= 3
                ? 3
                : this.items,
          },

          1280: {
            slidesPerView:
              this.expanded &&
              this.total > this.items
                ? this.items + 1
                : this.items,
          },
        },
      };

      if (typeof this.slider !== "object") {
        slideContainer.classList.add(
          this.classes.swiper
        );

        slideInner.classList.remove(
          this.classes.grid
        );

        slideInner.classList.add(
          this.classes.swiperWrapper
        );

        if (smoothMode) {
          this.classList.add(
            "is-smooth-scroll",
            "is-slider-active"
          );

          /*
           * Existing arrows/fraction are unnecessary
           * in continuous mode.
           */
          if (slideControls) {
            slideControls.classList.add(
              this.classes.hidden
            );
          }
        } else {
          this.classList.remove(
            "is-smooth-scroll"
          );

          this.classList.add(
            "is-slider-active"
          );

          if (slideControls) {
            slideControls.classList.remove(
              this.classes.hidden
            );
          }
        }

        this.slider = new MinimogLibs.Swiper(
          slideContainer,
          this.options
        );
      }
    }

    destroySlider() {
      const {
        slideContainer,
        slideControls,
        slideInner,
      } = queryDomNodes(this.selectors, this);

      if (slideContainer) {
        slideContainer.classList.remove(
          this.classes.swiper
        );
      }

      if (slideInner) {
        slideInner.classList.remove(
          this.classes.swiperWrapper
        );

        slideInner.classList.add(
          this.classes.grid
        );
      }

      if (slideControls) {
        slideControls.classList.add(
          this.classes.hidden
        );
      }

      this.classList.remove(
        "is-slider-active",
        "is-smooth-scroll"
      );

      if (typeof this.slider === "object") {
        this.slider.destroy(true, true);
        this.slider = false;
      }
    }
  }

  customElements.define(
    "m-collection-list",
    MCollectionList
  );
}